import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type {
  WorldBoss,
  WorldBossAttackResponse,
  WorldBossAttackResult,
  WorldBossHistoryEntry,
  WorldBossPage,
} from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, Panel, TypeChip } from '../components/ui';
import { BossBattle } from '../components/worldboss/BossBattle';
import { BossLeaderboard, BossNearby } from '../components/worldboss/BossLeaderboard';
import { FighterPicker } from '../components/worldboss/FighterPicker';
import { formatNumber, formatDate } from '../lib/format';

/**
 * The World Boss page — design PART 35.
 *
 * **"World Boss" is the name of a community fight, not a place.** There is no
 * map here and nothing on this page travels anywhere: it hangs off the Events
 * framework, the battle engine and the Collection, all of which already exist.
 *
 * The order down the page is the order the questions get asked. What is it and
 * how much is left; how long have we got; who is fighting for me and can I
 * still change them; how many swings do I have and what happens if I take one;
 * where am I; who is winning; what do I get. Anything that would have been a
 * second question about the same thing is folded into the first one — the
 * fighter panel carries the lock rule rather than putting the rule in a
 * footnote nobody reads.
 */

export function WorldBossPage() {
  const { refresh, celebrate, celebrateBadges } = useSession();
  const { push } = useToast();
  const state = useAsync<WorldBossPage>(() => api.get('/world-boss'));
  const [picking, setPicking] = useState(false);
  const [attacking, setAttacking] = useState(false);
  const [battle, setBattle] = useState<WorldBossAttackResult | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (state.loading && !state.data) return <div className="pxwell skeleton" style={{ height: 320 }} />;

  const boss = state.data?.boss ?? null;
  const history = state.data?.history ?? [];

  if (!boss) {
    return (
      <div className="stack">
        <div className="page-head">
          <h1 className="page-head__title">World Boss</h1>
        </div>
        <EmptyState art="⚡" title="Nothing is stirring">
          When a Legendary appears, every trainer picks one Pokémon and the whole server fights it
          together. The five who do the most damage keep it.
        </EmptyState>
        <History entries={history} />
      </div>
    );
  }

  async function attack() {
    setAttacking(true);
    try {
      const response = await api.post<WorldBossAttackResponse>('/world-boss/attack');
      setBattle(response.result);
      // The page is refreshed *behind* the replay rather than after it: by the
      // time somebody closes the modal the board is already current, including
      // whatever everybody else did while they were watching.
      state.reload();
      void refresh();
      celebrate(response.result.unlocked ?? []);
      celebrateBadges(response.result.badges ?? []);
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not attack.');
    } finally {
      setAttacking(false);
    }
  }

  const live = boss.status === 'LIVE';
  const finalising = boss.status === 'FINALIZING';
  const settled = boss.status === 'DEFEATED' || boss.status === 'ESCAPED';
  const attempts = boss.you.attempts;

  const canAttack =
    live && !boss.attacksPaused && boss.you.fighter != null && attempts.available > 0;

  return (
    <div className="stack wb-page">
      {battle && (
        <BossBattle
          events={battle.events}
          fighterSprite={boss.you.fighter?.sprite ?? null}
          bossSprite={boss.boss.sprite}
          bossName={boss.boss.name}
          damage={battle.damage}
          outcome={battle.outcome}
          totalDamage={battle.totalDamage}
          rank={battle.rank}
          nextRank={battle.nextRank}
          milestones={battle.milestones}
          phase={battle.phase}
          onClose={() => setBattle(null)}
        />
      )}

      {picking && (
        <FighterPicker
          bossName={boss.boss.name}
          bossTypes={boss.boss.types}
          currentId={boss.you.fighter?.id ?? null}
          onChosen={() => state.reload()}
          onClose={() => setPicking(false)}
        />
      )}

      {/* --- The boss itself. ------------------------------------------------ */}

      <section className={`pxframe wb-hero wb-hero--${boss.boss.types[0] ?? 'normal'}`}>
        <div className="wb-hero__label">World Boss</div>

        <div className="wb-hero__body">
          <img className="pixel wb-hero__sprite" src={boss.boss.sprite ?? ''} alt="" />

          <div className="wb-hero__facts">
            <h1 className="wb-hero__name">{boss.boss.name}</h1>
            {boss.title && <div className="wb-hero__title">{boss.title}</div>}
            <div className="row" style={{ gap: 4 }}>
              {boss.boss.types.map((type) => (
                <TypeChip key={type} type={type} />
              ))}
              <span className="chip">Lv. {boss.boss.level}</span>
              {boss.boss.phase && <span className="chip chip--gold">{boss.boss.phase.name}</span>}
            </div>

            {/* Banded, never exact. The boss is a preparation puzzle (PART 30),
                and a stat sheet turns it into arithmetic everybody solves the
                same way. */}
            <dl className="wb-hero__threat">
              <div>
                <dt>Attack</dt>
                <dd>{boss.boss.threat.attack}</dd>
              </div>
              <div>
                <dt>Defence</dt>
                <dd>{boss.boss.threat.defense}</dd>
              </div>
              <div>
                <dt>Speed</dt>
                <dd>{boss.boss.threat.speed}</dd>
              </div>
            </dl>
          </div>
        </div>

        {boss.intro && <p className="wb-hero__intro">{boss.intro}</p>}

        <div className="wb-hero__hp">
          <Bar ratio={boss.hp.percent / 100} variant="egg" tall />
          <div className="wb-hero__hp-figures">
            <strong className="mono">
              {formatNumber(boss.hp.remaining)} / {formatNumber(boss.hp.max)}
            </strong>
            <span className="mono">{boss.hp.percent.toFixed(1)}%</span>
          </div>
        </div>

        <div className="wb-hero__meta">
          <span>{live ? countdown(boss.secondsRemaining) : statusLine(boss)}</span>
          <span>
            {formatNumber(boss.totals.attackers)} trainers ·{' '}
            {formatNumber(boss.totals.totalDamage)} total damage
          </span>
        </div>

        {/* PART 8: an admin who moved the target mid-fight has to be visible to
            the people fighting it, not only in an audit table. */}
        {boss.hp.adjustedAt && (
          <p className="tiny wb-hero__note">
            Staff adjusted this boss's health on {formatDate(boss.hp.adjustedAt)}.
          </p>
        )}

        {boss.attacksPaused && live && (
          <p className="tiny wb-hero__note">Attacks are paused while staff look at something.</p>
        )}

        {finalising && (
          <p className="tiny wb-hero__note">
            Results are being finalised. Ranks are locked and no more attacks are accepted.
          </p>
        )}
      </section>

      {/* --- Your reward, when there is one to talk about. -------------------- */}

      {boss.you.reward && <RewardNotice boss={boss} />}

      {/* --- Your fighter and your attempts. ---------------------------------- */}

      <div className="wb-columns">
        <Panel
          title="Your fighter"
          hint={boss.you.fighter ? `Lv. ${boss.you.fighter.level}` : 'Not chosen'}
          action={
            live && !boss.you.lock.locked ? (
              <Button size="sm" onClick={() => setPicking(true)}>
                {boss.you.fighter ? 'Change' : 'Choose'}
              </Button>
            ) : undefined
          }
        >
          {boss.you.fighter ? (
            <div className="stack-sm">
              <div className="row" style={{ gap: 'var(--gap)' }}>
                <img className="pixel" src={boss.you.fighter.sprite ?? ''} alt="" width={72} height={72} />
                <div className="stack-sm" style={{ flex: 1 }}>
                  <strong>
                    {boss.you.fighter.isShiny && <span style={{ color: 'var(--gold-dark)' }}>✦ </span>}
                    {boss.you.fighter.name}
                  </strong>
                  <div className="row" style={{ gap: 4 }}>
                    {boss.you.fighter.types.map((type) => (
                      <TypeChip key={type} type={type} />
                    ))}
                  </div>
                  <span className="tiny muted">
                    {boss.you.fighter.natureName ? `${boss.you.fighter.natureName} · ` : ''}
                    Potential {boss.you.fighter.ivPercent}%
                  </span>
                </div>
              </div>

              <dl className="wb-fighter__stats">
                <div>
                  <dt>Atk</dt>
                  <dd className="mono">{boss.you.fighter.stats.attack}</dd>
                </div>
                <div>
                  <dt>Def</dt>
                  <dd className="mono">{boss.you.fighter.stats.defense}</dd>
                </div>
                <div>
                  <dt>Sp. Atk</dt>
                  <dd className="mono">{boss.you.fighter.stats.specialAttack}</dd>
                </div>
                <div>
                  <dt>Sp. Def</dt>
                  <dd className="mono">{boss.you.fighter.stats.specialDefense}</dd>
                </div>
                <div>
                  <dt>Spe</dt>
                  <dd className="mono">{boss.you.fighter.stats.speed}</dd>
                </div>
                <div>
                  <dt>HP</dt>
                  <dd className="mono">{boss.you.fighter.stats.hp}</dd>
                </div>
              </dl>

              {/* The lock rule where the lock is, not in a footnote. */}
              <p className="tiny muted">{boss.you.lock.description}</p>
            </div>
          ) : (
            <EmptyState art="◇" title="Choose one Pokémon">
              One Pokémon represents you for this whole fight. Typing, level, genetics and bulk all
              matter — pick the one that answers {boss.boss.name}.
            </EmptyState>
          )}
        </Panel>

        <Panel
          title="Battle"
          hint={live ? `${attempts.available} of ${attempts.perDay} attempts` : undefined}
        >
          <div className="stack-sm">
            <div className="wb-attempts" role="img" aria-label={`${attempts.available} attempts left`}>
              {Array.from({ length: attempts.perDay }, (_, index) => (
                <span
                  key={index}
                  className={`wb-attempts__pip${index < attempts.available ? ' wb-attempts__pip--full' : ''}`}
                />
              ))}
              {/* Bonus attempts render beyond the daily row, because they are a
                  different thing: dailies expire and these do not. */}
              {Array.from({ length: attempts.bonus }, (_, index) => (
                <span key={`bonus-${index}`} className="wb-attempts__pip wb-attempts__pip--bonus" />
              ))}
            </div>

            <p className="tiny muted">
              {attempts.available > 0
                ? `${attempts.available} ${attempts.available === 1 ? 'attempt' : 'attempts'} left.`
                : 'No attempts left today.'}{' '}
              Resets {relative(attempts.resetsAt)}.
            </p>

            <Button onClick={attack} disabled={!canAttack || attacking}>
              {attacking
                ? 'Fighting…'
                : !live
                  ? 'The fight is over'
                  : !boss.you.fighter
                    ? 'Choose a fighter first'
                    : attempts.available === 0
                      ? 'No attempts left'
                      : `Challenge ${boss.boss.name}`}
            </Button>

            <dl className="wb-yours">
              <div>
                <dt>Your damage</dt>
                <dd className="mono">{formatNumber(boss.you.damage)}</dd>
              </div>
              <div>
                <dt>Your rank</dt>
                <dd className="mono">{boss.you.rank ? `#${boss.you.rank}` : '—'}</dd>
              </div>
              <div>
                <dt>Best hit</dt>
                <dd className="mono">{formatNumber(boss.you.bestDamage)}</dd>
              </div>
              {boss.you.toRewardZone != null && (
                <div>
                  <dt>To the reward zone</dt>
                  <dd className="mono">{formatNumber(boss.you.toRewardZone)}</dd>
                </div>
              )}
            </dl>
          </div>
        </Panel>
      </div>

      {/* --- Where you stand — PART 10. ---------------------------------------- */}

      {boss.you.nearby.length > 1 && (
        <Panel title="Around you" hint={boss.you.rank ? `#${boss.you.rank}` : undefined}>
          <BossNearby rows={boss.you.nearby} zone={boss.rewards.zone} />
        </Panel>
      )}

      {/* --- The ladder — PART 9. ---------------------------------------------- */}

      <Panel
        title="Top damage"
        hint={`${formatNumber(boss.totals.attackers)} trainers fighting`}
      >
        <BossLeaderboard rows={boss.board} zone={boss.rewards.zone} settled={settled} />
      </Panel>

      {/* --- What is on the line. ---------------------------------------------- */}

      <Panel title="Rewards">
        <div className="stack-sm">
          {boss.rewards.legendary ? (
            <div className="pxwell wb-prize">
              <img
                className="pixel wb-prize__sprite"
                src={boss.rewards.legendary.sprite ?? ''}
                alt=""
                width={80}
                height={80}
              />
              <div>
                <div className="wb-prize__zone">
                  Ranks #{boss.rewards.zone.from}–#{boss.rewards.zone.to}
                </div>
                <strong>
                  {boss.rewards.legendary.isShiny && (
                    <span style={{ color: 'var(--gold-dark)' }}>✦ Shiny </span>
                  )}
                  {boss.rewards.legendary.name}
                </strong>
                <div className="tiny muted">Level {boss.rewards.legendary.level}</div>
              </div>
            </div>
          ) : (
            <p className="tiny muted">
              This fight pays no Legendary — it is a preseason run. Milestone rewards still apply.
            </p>
          )}

          {/* PART 18, stated up front rather than discovered on the last
              evening. */}
          <p className="tiny muted">
            {boss.rewards.grantOnEscape
              ? 'The reward is paid whether or not the boss is defeated.'
              : `The reward is only paid if the server defeats ${boss.boss.name}.`}
            {boss.rewards.minContribution > 0 &&
              ` A minimum of ${formatNumber(boss.rewards.minContribution)} damage is needed to qualify.`}
          </p>

          {boss.rewards.milestones.length > 0 && (
            <ul className="wb-milestones">
              {boss.rewards.milestones.map((milestone) => (
                <li
                  key={milestone.damage}
                  className={`wb-milestones__item${milestone.reached ? ' wb-milestones__item--done' : ''}`}
                >
                  <span className="mono">{formatNumber(milestone.damage)}</span>
                  <span>{milestone.label}</span>
                  <span className="tiny muted">{milestone.reached ? 'Reached' : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Panel>

      {/* --- The rules, published before the event — PART 22. ------------------ */}

      <Panel title="Rules" collapsibleKey="worldboss-rules" defaultCollapsed>
        <div className="stack-sm tiny">
          <p>{boss.rules.fighterLock}</p>
          <p>{boss.rules.attempts}</p>
          <p>{boss.rules.guestClaim}</p>
          <div>
            <strong>Ties are broken by:</strong>
            <ol className="wb-rules__list">
              {boss.rules.tieBreaks.map((rule) => (
                <li key={rule}>{rule}</li>
              ))}
            </ol>
          </div>
          {boss.boss.phases.length > 0 && (
            <p>
              {boss.boss.name} changes as it weakens:{' '}
              {boss.boss.phases.map((phase) => `${phase.name} below ${phase.atPercent}%`).join(', ')}.
            </p>
          )}
          <p className="muted">
            Every battle is resolved on the server. Damage, critical hits and rank are decided there
            and your browser only plays back what happened.
          </p>
        </div>
      </Panel>

      <History entries={history} />
    </div>
  );
}

/**
 * The one place the page tells somebody what happened to their reward.
 *
 * Four states, and none of them is "no". A guest is told exactly what to do and
 * by when (PART 25); a held placing is told a human is looking (PART 26); a
 * forfeited one is told plainly rather than left as a mystery.
 */
function RewardNotice({ boss }: { boss: WorldBoss }) {
  const reward = boss.you.reward!;
  if (reward.status === 'GRANTED') {
    return (
      <div className="pxframe wb-notice wb-notice--good">
        <strong>You finished #{reward.rank}.</strong> {boss.rewards.legendary?.name ?? 'Your reward'} is
        in your collection.{' '}
        <Link to="/collection">Go and look at it</Link>.
      </div>
    );
  }
  if (reward.status === 'PENDING_CLAIM') {
    return (
      <div className="pxframe wb-notice wb-notice--warn">
        <strong>You finished #{reward.rank} — your reward is waiting.</strong> Claim your trainer
        account{reward.deadline ? ` before ${formatDate(reward.deadline)}` : ''} to receive it.{' '}
        <Link to="/claim">Claim your account</Link>.
      </div>
    );
  }
  if (reward.status === 'HELD') {
    return (
      <div className="pxframe wb-notice wb-notice--warn">
        <strong>You finished #{reward.rank}.</strong> Your reward is being checked by staff and will
        arrive shortly.
      </div>
    );
  }
  return (
    <div className="pxframe wb-notice">
      <strong>You finished #{reward.rank}.</strong> This reward is no longer available.
    </div>
  );
}

/** The public archive — PART 56. */
function History({ entries }: { entries: WorldBossHistoryEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <Panel title="World Boss history" hint={`${entries.length} on record`}>
      <ul className="wb-history">
        {entries.map((entry) => (
          <li key={entry.id} className="pxwell wb-history__item">
            <img className="pixel" src={entry.sprite ?? ''} alt="" width={56} height={56} />
            <div className="wb-history__body">
              <strong>
                {entry.bossName}
                {entry.title && ` — ${entry.title}`}
              </strong>
              <div className="tiny muted">
                {entry.outcome === 'DEFEATED' ? 'Defeated' : 'Escaped'} · {formatDate(entry.at)} ·{' '}
                {formatNumber(entry.participants)} trainers ·{' '}
                {formatNumber(entry.totalDamage)} damage
              </div>
              {entry.winners.length > 0 && (
                <ol className="wb-history__winners">
                  {entry.winners.map((winner) => (
                    <li key={winner.rank}>
                      <span className="mono">#{winner.rank}</span>{' '}
                      <Link to={`/trainer/${winner.username}`}>{winner.trainerName}</Link>{' '}
                      <span className="tiny muted mono">{formatNumber(winner.damage)}</span>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function statusLine(boss: WorldBoss): string {
  if (boss.status === 'DEFEATED') return `${boss.boss.name} was defeated!`;
  if (boss.status === 'ESCAPED') return `${boss.boss.name} escaped.`;
  if (boss.status === 'FINALIZING') return 'Results finalising…';
  return 'Not yet begun';
}

function countdown(seconds: number): string {
  if (seconds <= 0) return 'Ending now';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `Ends in ${days}d ${hours}h`;
  if (hours > 0) return `Ends in ${hours}h ${minutes}m`;
  return `Ends in ${minutes}m`;
}

function relative(iso: string): string {
  const seconds = Math.max(0, Math.floor((new Date(iso).getTime() - Date.now()) / 1000));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}
